import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { visitsAPI, reportsAPI } from "../../../shared/lib/api";
import { useAuth } from "../../../shared/context/AuthContext";
import { Button } from "../../../shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../shared/components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  AlertTriangle,
  Building2,
  Clock,
  Activity,
  ArrowLeft,
  Eye,
  RefreshCw,
} from "lucide-react";
import {
  formatDateTime,
  getTimeSince,
  PURPOSE_LABELS,
  ROLE_LABELS,
} from "../../../shared/lib/utils";

export default function SupervisorDashboardPage() {
  const navigate = useNavigate();
  const { isSupervisor } = useAuth();

  const [activePersonnel, setActivePersonnel] = useState(null);
  const [incompleteVisits, setIncompleteVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isSupervisor()) {
      toast.error("Access denied. Supervisor role required.");
      navigate("/");
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [personnelRes, incompleteRes] = await Promise.all([
        reportsAPI.getActivePersonnel(),
        visitsAPI.getIncomplete(),
      ]);

      setActivePersonnel(personnelRes.data);
      setIncompleteVisits(incompleteRes.data);
    } catch (err) {
      toast.error("Failed to load supervisor data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="supervisor-dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="font-mono text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-black text-2xl">
            Supervisor Dashboard
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-none border-2 font-mono"
          data-testid="refresh-btn"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-2 border-emerald-500 rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Active Personnel
                </p>
                <p className="font-heading font-black text-4xl text-emerald-500">
                  {activePersonnel?.total_active || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-emerald-500/20 flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-500 rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Incomplete Entries
                </p>
                <p className="font-heading font-black text-4xl text-amber-500">
                  {incompleteVisits.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-border rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Stations Active
                </p>
                <p className="font-heading font-black text-4xl">
                  {activePersonnel?.stations?.length || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-muted flex items-center justify-center">
                <Building2 className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="rounded-none h-12 p-1 bg-muted w-full md:w-auto">
          <TabsTrigger
            value="active"
            className="rounded-none font-mono uppercase tracking-wider text-xs data-[state=active]:bg-background"
          >
            Active Personnel
          </TabsTrigger>
          <TabsTrigger
            value="incomplete"
            className="rounded-none font-mono uppercase tracking-wider text-xs data-[state=active]:bg-background"
          >
            Incomplete Entries
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activePersonnel?.stations?.length === 0 ? (
            <Card className="border-2 border-dashed border-border rounded-none">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="font-body text-muted-foreground">
                  No personnel currently in stations
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activePersonnel?.stations?.map((station) => (
                <Card
                  key={station.station_id}
                  className="border-2 border-border rounded-none"
                  data-testid={`station-personnel-${station.station_id}`}
                >
                  <CardHeader className="border-b border-border bg-muted/20 p-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading font-bold flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-[#FF6B00]" />
                        {station.station_name}
                      </CardTitle>
                      <Badge className="rounded-none bg-emerald-500 text-white font-mono text-xs">
                        {station.personnel.length} ACTIVE
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="font-mono text-xs uppercase tracking-widest text-muted-foreground p-3 text-left">
                            Name
                          </th>
                          <th className="font-mono text-xs uppercase tracking-widest text-muted-foreground p-3 text-left">
                            Role
                          </th>
                          <th className="font-mono text-xs uppercase tracking-widest text-muted-foreground p-3 text-left">
                            Purpose
                          </th>
                          <th className="font-mono text-xs uppercase tracking-widest text-muted-foreground p-3 text-left">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {station.personnel.map((person, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-border last:border-0"
                          >
                            <td className="font-mono text-sm p-3">
                              {person.user_name}
                            </td>
                            <td className="font-mono text-sm p-3">
                              <Badge
                                variant="outline"
                                className="rounded-none text-xs"
                              >
                                {ROLE_LABELS[person.user_role] || person.user_role}
                              </Badge>
                            </td>
                            <td className="font-mono text-sm p-3">
                              {PURPOSE_LABELS[person.purpose] || person.purpose}
                            </td>
                            <td className="font-mono text-sm p-3 text-muted-foreground">
                              {getTimeSince(person.entry_time)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="incomplete" className="mt-6">
          {incompleteVisits.length === 0 ? (
            <Card className="border-2 border-dashed border-border rounded-none">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="font-body text-muted-foreground">
                  No incomplete entries
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {incompleteVisits.map((visit) => (
                <Card
                  key={visit.id}
                  className="border-2 border-amber-500 rounded-none bg-amber-500/5"
                  data-testid={`incomplete-visit-${visit.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-heading font-bold">
                            {visit.user_name}
                          </span>
                          <Badge className="rounded-none bg-amber-500 text-white font-mono text-xs">
                            INCOMPLETE
                          </Badge>
                        </div>
                        <p className="font-mono text-sm text-muted-foreground">
                          {visit.station_name}
                        </p>
                        <p className="font-mono text-xs text-muted-foreground mt-1">
                          Entry: {formatDateTime(visit.entry_time)} •{" "}
                          {getTimeSince(visit.entry_time)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/visit/${visit.id}`)}
                        className="rounded-none border-2 font-mono text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
